/* =============================================================================
   VIEWS / CUSTOMER-TABLE-CART — der vorläufige Warenkorb des Tisches
   =============================================================================
   S244 Cut B, erweitert in S251. Die Rechen- und Gruppier-Logik liegt hier PURE
   (ohne DOM), damit Vitest sie ohne Browser prüfen kann; das Rendern ist ein
   dünner Aufsatz.

   Der Warenkorb gehört dem TISCH, nicht dem Handy: jeder sieht alles, aber
   ändern darf jeder nur seine eigenen Zeilen. Das ist keine technische Hürde,
   sondern eine soziale — wer die Pommes des Nachbarn löschen kann, sorgt für
   Ärger am Tisch. Der Server hält dieselbe Regel (`table_cart_*` prüfen den
   guest_key), diese Datei macht sie nur sichtbar.

   S251 — dieser Warenkorb IST der vorläufige Stand des Tisches, und er ist der
   einzige Ort, an dem abgesendet wird. Was hier steht, geht als EINE Runde raus
   (`submit_table_cart` sendet alles der Belegung, mit Advisory-Lock). Deshalb
   muss vorher sichtbar sein, was mitgeht — nach Gast gruppiert, mit
   Zwischensummen, und bei fremden Positionen mit einer ausdrücklichen Rückfrage.
============================================================================ */

import { htmlEscape, fmtEUR } from '../utils/helpers.js';

/**
 * Anzeigename einer Gäste-Gruppe.
 *
 * Die eigene Gruppe heißt „Du" — auch wenn ein Vorname hinterlegt ist: den
 * trägt die Kopfzeile der Seite schon („Du bestellst als Anna"), und im
 * Warenkorb ist die Frage nicht „wer bin ich", sondern „was ist meins".
 * Fremde ohne Vornamen werden durchnummeriert; `Gast 2` ist freundlicher als
 * ein UUID-Fragment und verrät nichts.
 *
 * @param {object} entry  Gruppe (`mine`, `guest_name`)
 * @param {number} index  Anzeigenummer minus eins — für fremde Gäste vergibt
 *                        `groupCartByGuest` sie ab 1, also „Gast 2" aufwärts:
 *                        die 1 gehört dem Gerät, das gerade draufschaut.
 */
export function guestLabel(entry, index) {
  if (entry && entry.mine) return 'Du';
  const name = entry && entry.guest_name;
  if (name && String(name).trim()) return String(name).trim();
  return `Gast ${index + 1}`;
}

/**
 * Warenkorb nach Gast gruppieren.
 *
 * Die eigene Gruppe steht vorn (S251) — wer den Warenkorb öffnet, sucht zuerst
 * seine eigenen Zeilen, und nur die kann er ändern. Alle anderen behalten die
 * Reihenfolge ihres Erst-Auftretens, damit die Liste beim Poll nicht springt:
 * jede Umsortierung wäre für den Leser ein Wackeln ohne Grund.
 *
 * Grenze der Nummerierung, bewusst in Kauf genommen: sie leitet sich aus den
 * vorhandenen Zeilen ab (kein Zähler im Client, keine neue Spalte am Server —
 * Jonas-Entscheid). Löscht „Gast 2" alle seine Zeilen, rückt „Gast 3" nach.
 * Der Alternative — eine Nummer je `guest_key` im localStorage — fehlt der
 * Bezug zur Belegung; sie liefe über Tischwechsel hinweg weiter und wäre damit
 * schlechter als das Nachrücken.
 *
 * @param {Array} cart  Zeilen aus `get_table_session`
 * @returns {Array<{guestKey, label, mine, lines, totalCents}>}
 */
export function groupCartByGuest(cart) {
  const rows = Array.isArray(cart) ? cart : [];
  const order = [];
  const byKey = new Map();
  for (const r of rows) {
    if (!r) continue;
    const key = r.guest_key || '?';
    if (!byKey.has(key)) {
      byKey.set(key, { guestKey: key, guest_name: r.guest_name, mine: !!r.mine, lines: [], totalCents: 0 });
      order.push(key);
    }
    const g = byKey.get(key);
    // Der Vorname kann nachgereicht werden — die zuletzt gesehene nicht-leere
    // Fassung gewinnt, sonst bliebe die erste Zeile für immer „Gast 2".
    if (r.guest_name && String(r.guest_name).trim()) g.guest_name = r.guest_name;
    if (r.mine) g.mine = true;
    g.lines.push(r);
    g.totalCents += Number(r.line_total_cents) || 0;
  }
  const groups = order.map(key => byKey.get(key));
  const mine = groups.filter(g => g.mine);
  const others = groups.filter(g => !g.mine);
  // Fremde starten bei Anzeigenummer 2 — die 1 ist „Du", ob dieses Gerät nun
  // schon etwas eingelegt hat oder nicht.
  return mine.map(g => ({ ...g, label: guestLabel(g, 0) }))
    .concat(others.map((g, i) => ({ ...g, label: guestLabel(g, i + 1) })));
}

/** Positionen + Summe des ganzen Tisches. */
export function cartSummary(cart) {
  const rows = Array.isArray(cart) ? cart : [];
  return {
    count: rows.reduce((s, r) => s + (Number(r && r.qty) || 0), 0),
    lines: rows.length,
    totalCents: rows.reduce((s, r) => s + (Number(r && r.line_total_cents) || 0), 0)
  };
}

/** Text der Sticky-Leiste unten. Pure, damit der Wortlaut testbar ist. */
export function cartBarText(cart) {
  const s = cartSummary(cart);
  if (!s.lines) return 'Noch nichts im Warenkorb';
  const pos = s.lines === 1 ? '1 Position' : `${s.lines} Positionen`;
  return `${pos} · ${fmtEUR(s.totalCents / 100)}`;
}

/**
 * Pure — welche Runde schickt dieser Absendevorgang los?
 *
 * Reine ANZEIGE. Die Wahrheit vergibt `submit_table_cart` (`max(round_no) + 1`,
 * unter Advisory-Lock); tippen zwei Handys gleichzeitig, kann die genannte
 * Nummer daneben liegen. Darüber wird nicht gestritten — der Knopf soll dem
 * Gast sagen „das ist der nächste Schwung", nicht eine Zusage machen.
 */
export function nextRoundNo(order) {
  const rounds = (order && Array.isArray(order.rounds)) ? order.rounds : [];
  let max = 0;
  for (const r of rounds) {
    const n = Number(r && r.round_no) || 0;
    if (n > max) max = n;
  }
  if (!max && order) {
    // Kein `rounds`-Array (älterer Server) — dann aus den Positionen ableiten.
    for (const i of (Array.isArray(order.items) ? order.items : [])) {
      const n = Number(i && i.round_no) || 0;
      if (n > max) max = n;
    }
  }
  return max + 1;
}

/**
 * Pure — die fremden Anteile im Warenkorb, für die Rückfrage vor dem Absenden.
 *
 * @returns {Array<{label:string, lines:number, cents:number}>} leer, wenn nur
 *          eigene Positionen drin sind.
 */
export function foreignSubmitParts(cart) {
  return groupCartByGuest(cart)
    .filter(g => !g.mine)
    .map(g => ({ label: g.label, lines: g.lines.length, cents: g.totalCents }));
}

/**
 * Pure — Text der Rückfrage, oder `null`, wenn keine nötig ist.
 *
 * Rückgefragt wird NUR, wenn fremde Positionen mitgehen. Wer allein seine
 * eigenen zwei Zeilen abschickt, soll nicht jedes Mal einen Dialog wegtippen;
 * wer die halbfertige Auswahl des Tisches mitschickt, soll wissen, wessen und
 * wie viel — deshalb stehen Namen UND Beträge drin.
 */
export function submitConfirmBody(cart) {
  const parts = foreignSubmitParts(cart);
  if (!parts.length) return null;
  const list = parts.map(p =>
    `${p.label} (${p.lines === 1 ? '1 Position' : p.lines + ' Positionen'} · ${fmtEUR(p.cents / 100)})`
  ).join(' · ');
  return `Das schickt auch: ${list}`;
}

/** Maximale Länge eines Küchen-Hinweises — dieselbe Zahl wie der CHECK in
 *  phase_185. Steht hier, damit Feld, Zähler und Test aus einer Quelle lesen. */
export const NOTE_MAX = 200;

/**
 * Pure — der eigene Hinweis aus dem Session-Payload (S253).
 *
 * `guest_notes` kommt aus `get_table_session` und führt fremde Hinweise mit,
 * aber ohne fremden `guest_key`; „meiner" erkennt der Client am `mine`-Flag.
 */
export function myGuestNote(session) {
  const list = (session && Array.isArray(session.guest_notes)) ? session.guest_notes : [];
  const hit = list.find(n => n && n.mine);
  return hit ? String(hit.note || '') : '';
}

/** Pure — die Hinweise der ANDEREN, in Anzeige-Reihenfolge. */
export function foreignGuestNotes(session) {
  const list = (session && Array.isArray(session.guest_notes)) ? session.guest_notes : [];
  return list.filter(n => n && !n.mine && String(n.note || '').trim());
}

/**
 * Pure — Text des Zeichenzählers, oder `''`, solange er nicht nötig ist.
 *
 * Erst ab 150 Zeichen: ein Zähler, der von der ersten Taste an mitläuft, macht
 * aus einer freien Notiz eine Prüfung. Wer in die Nähe der Grenze kommt, soll
 * sie sehen — vorher nicht.
 */
export function noteCounterText(value) {
  const n = String(value || '').length;
  if (n < 150) return '';
  return `${n}/${NOTE_MAX}`;
}

// -----------------------------------------------------------------------------
// Render (dünner Aufsatz über den pure Helfern)
// -----------------------------------------------------------------------------

/**
 * Eine Position.
 *
 * S251 (U5) — Beilagen, Extras und Notizen liegen hinter „Details". Die Zeile
 * selbst zeigt Name, Menge, Preis; ein Burger mit sechs Anpassungen füllte
 * sonst den halben Bildschirm, und der Gast sucht beim Durchsehen zuerst die
 * Summe. Der offene/zugeklappte Zustand kommt von aussen (`openLines`), damit
 * ein Poll-Tick nichts zuklappt.
 */
function renderLine(line, editable, openLines) {
  const detail = lineDetailText(line);
  const name = (line.item && line.item.name) || 'Position';
  const id = String(line.id || '');
  const open = !!(openLines && openLines.has(id));
  return `
    <div class="ct-line">
      <div class="ct-line-main">
        <div class="ct-line-name">${line.qty}× ${htmlEscape(name)}</div>
        ${detail ? `
          <button type="button" class="ct-line-more" aria-expanded="${open ? 'true' : 'false'}"
                  onclick="toggleCartLine('${htmlEscape(id)}')">
            ${open ? 'Details ausblenden' : 'Details'}
          </button>
          ${open ? `<div class="ct-line-detail">${htmlEscape(detail)}</div>` : ''}` : ''}
      </div>
      <div class="ct-line-price">${htmlEscape(fmtEUR((Number(line.line_total_cents) || 0) / 100))}</div>
      ${editable ? `
        <div class="ct-line-actions">
          <button type="button" class="ct-qty" aria-label="Weniger"
                  onclick="tableCartSetQty('${htmlEscape(id)}', ${Math.max(1, (Number(line.qty) || 1) - 1)})">−</button>
          <span class="ct-qty-val">${line.qty}</span>
          <button type="button" class="ct-qty" aria-label="Mehr"
                  onclick="tableCartSetQty('${htmlEscape(id)}', ${Math.min(20, (Number(line.qty) || 1) + 1)})">+</button>
          <button type="button" class="ct-remove" aria-label="Entfernen"
                  onclick="tableCartRemove('${htmlEscape(id)}')">🗑</button>
        </div>` : ''}
    </div>`;
}

/**
 * Eine Gäste-Gruppe, auf- und zuklappbar (S251/U4).
 *
 * Die Kopfzeile trägt Name, Anzahl und Zwischensumme — damit auch zugeklappt
 * alles Wesentliche dasteht. Zugeklappt wird der Rumpf gar nicht erst gebaut:
 * ein `hidden`-Block, den niemand sieht, ist nur DOM, das bei jedem Poll neu
 * verglichen werden müsste.
 */
function renderGroup(g, collapsed, openLines, ownNoteHtml) {
  const open = !collapsed;
  const pos = g.lines.length === 1 ? '1 Position' : `${g.lines.length} Positionen`;
  return `
    <section class="ct-group${g.mine ? ' is-mine' : ''}">
      <button type="button" class="ct-group-head" aria-expanded="${open ? 'true' : 'false'}"
              onclick="toggleCartGroup('${htmlEscape(String(g.guestKey))}')">
        <span class="ct-group-caret">${open ? '▾' : '▸'}</span>
        <span class="ct-group-name">${htmlEscape(g.label)}</span>
        <span class="ct-group-meta">${htmlEscape(pos)} · ${htmlEscape(fmtEUR(g.totalCents / 100))}</span>
      </button>
      ${open ? `<div class="ct-group-body">${g.lines.map(l => renderLine(l, g.mine, openLines)).join('')}</div>` : ''}
      ${ownNoteHtml || ''}
    </section>`;
}

/**
 * Das Eingabefeld für den eigenen Küchen-Hinweis (S253).
 *
 * Es steht in der eigenen Gruppe und AUSSERHALB des Auf-/Zuklapp-Bereichs: eine
 * Allergie-Angabe darf nicht hinter einem zugeklappten Block verschwinden.
 * Gespeichert wird verzögert und zusätzlich beim Verlassen des Feldes — der
 * letzte Tastendruck vor dem Wegtippen darf nicht verloren gehen (Memory
 * `feedback_gg_template_autosave_trailing_race`).
 */
function renderNoteEditor(value) {
  const v = String(value || '');
  return `
    <div class="ct-note">
      <label class="ct-note-label" for="ct-note-input">
        ✏️ Hinweis für die Küche <span>(Allergien, Wünsche)</span>
      </label>
      <textarea id="ct-note-input" class="ct-note-input" rows="2" maxlength="${NOTE_MAX}"
                placeholder="z. B. Nussallergie, bitte ohne Zwiebeln"
                oninput="tableNoteInput(this.value)"
                onblur="tableNoteBlur(this.value)">${htmlEscape(v)}</textarea>
      <div class="ct-note-count" id="ct-note-count">${htmlEscape(noteCounterText(v))}</div>
      <p class="ct-note-hint">Gilt für alle deine Runden an diesem Tisch — auch für spätere.</p>
    </div>`;
}

/**
 * Die Hinweise der anderen — als EIGENER Block unter den Gruppen.
 *
 * Bewusst NICHT je Gruppe: der Server gibt fremde `guest_key`s nicht heraus (er
 * soll nicht), eine Zuordnung „der Reihe nach" wäre also geraten. Schlimmer:
 * ein Gast, der nur eine Allergie eingetragen und noch nichts eingelegt hat,
 * hätte gar keine Gruppe — und sein Hinweis wäre unsichtbar. Genau der Fall,
 * in dem er am wichtigsten ist.
 *
 * Der Name steht dabei, wenn es einen gibt; sonst schlicht „Am Tisch".
 */
function renderForeignNotes(notes) {
  const rows = (Array.isArray(notes) ? notes : []).filter(n => n && String(n.note || '').trim());
  if (!rows.length) return '';
  return `
    <div class="ct-note is-foreign">
      <div class="ct-note-label">✏️ Hinweise der anderen</div>
      ${rows.map(n => `
        <div class="ct-note-read">
          <strong>${htmlEscape((n.name && String(n.name).trim()) || 'Am Tisch')}:</strong>
          ${htmlEscape(String(n.note).trim())}
        </div>`).join('')}
    </div>`;
}

/**
 * Inhalt des Slide-overs. `cart` kommt roh aus `get_table_session`.
 *
 * @param {Array}  cart
 * @param {object} [opts] `{ tableName, roundNo, collapsedGroups:Set, openLines:Set }`
 */
export function renderCartSheet(cart, opts = {}) {
  const tableName = opts.tableName;
  const groups = groupCartByGuest(cart);
  const s = cartSummary(cart);
  const head = `
    <div class="ct-sheet-head">
      <h2>Vorläufiger Warenkorb</h2>
      <button type="button" class="ct-close" onclick="closeTableCartSheet()" aria-label="Schließen">×</button>
    </div>
    <p class="ct-sheet-sub">Noch nicht bestellt${tableName ? ` · Tisch ${htmlEscape(String(tableName))}` : ''}</p>`;

  if (!groups.length) {
    // Auch ohne eigene Position: wer eine Allergie angeben will, bevor er
    // etwas auswählt, soll das können.
    //
    // S254 (B1 aus dem S253-Retest) — und die fremden Hinweise gehören GENAU
    // HIER hin. Nach jeder abgeschickten Runde ist der Warenkorb leer, dieser
    // Zweig ist also der Normalzustand zwischen zwei Runden — und ohne die
    // Zeile war die Allergie des Nachbarn dann unsichtbar, obwohl der Server
    // sie mitliefert. Live gemessen.
    return `${head}
      <p class="ct-empty">Noch nichts ausgewählt. Tippe in der Karte auf ein Gericht.</p>
      ${renderNoteEditor(opts.myNote != null ? opts.myNote : '')}
      ${renderForeignNotes(opts.foreignNotes || [])}`;
  }

  const collapsed = opts.collapsedGroups || new Set();
  const openLines = opts.openLines || new Set();
  const roundNo = Number(opts.roundNo) > 0 ? Number(opts.roundNo) : 1;
  // S253 — der eigene Hinweis steht als Feld in der eigenen Gruppe, die fremden
  // als eigener Lese-Block darunter (Begründung bei `renderForeignNotes`).
  const myNote = opts.myNote != null ? opts.myNote : '';
  const foreign = Array.isArray(opts.foreignNotes) ? opts.foreignNotes : [];
  // Hat dieses Gerät noch nichts eingelegt, gibt es keine eigene Gruppe — das
  // Feld muss trotzdem da sein.
  const hasOwnGroup = groups.some(g => g.mine);

  return `${head}
    <p class="ct-hint">Alle am Tisch sehen denselben Warenkorb. Ändern kannst du nur, was du selbst ausgewählt hast.</p>
    ${groups.map(g => renderGroup(
        g, collapsed.has(String(g.guestKey)), openLines,
        g.mine ? renderNoteEditor(myNote) : '')).join('')}
    ${hasOwnGroup ? '' : renderNoteEditor(myNote)}
    ${renderForeignNotes(foreign)}
    <div class="ct-total">
      <span>Tisch gesamt</span>
      <strong>${htmlEscape(fmtEUR(s.totalCents / 100))}</strong>
    </div>
    <button type="button" class="ct-submit" onclick="submitTableCart()">
      🍽️ Runde ${roundNo} für den Tisch absenden
    </button>
    <p class="ct-submit-hint">Der ganze Warenkorb geht als eine Runde in die Küche.</p>`;
}

/** Kurzbeschreibung einer Position (Extras/Beilagen), ohne Preis-Wiederholung. */
export function lineDetailText(line) {
  const c = (line && line.item && line.item.customizations) || {};
  const names = (arr) => (Array.isArray(arr) ? arr : []).map(x => x && x.name).filter(Boolean);
  const bits = [];
  if (c.bun && c.bun.name) bits.push(c.bun.name);
  if (c.patty && c.patty.name) bits.push(c.patty.name);
  if (c.dressing && c.dressing.name) bits.push(c.dressing.name);
  const removed = Array.isArray(c.removedIngredients) ? c.removedIngredients : [];
  if (removed.length) bits.push('ohne ' + removed.join(', '));
  const extras = names(c.extras);
  if (extras.length) bits.push('+ ' + extras.join(', '));
  const sauces = names(c.sauces);
  if (sauces.length) bits.push(sauces.join(', '));
  const sides = names(c.sides).concat(c.side && c.side.name ? [c.side.name] : []);
  if (sides.length) bits.push('Beilagen: ' + sides.join(', '));
  const dips = names(c.sideSauces);
  if (dips.length) bits.push('Dips: ' + dips.join(', '));
  if (line && line.item && line.item.notes) bits.push('„' + line.item.notes + '"');
  return bits.join(' · ');
}
